// Catalog.ID Client — The Vault's Own Account Blobs
// ============================================================================
// `cid` is a RESERVED namespace (cid-encrypted-storage §4.1): it holds the CID
// client's own synchronized state — the member's bitcash wallet document (§7),
// the registry-authority generation state (§5.2) — and no module may ever
// speak for it. `crypto/derive.ts` refuses it at registration and in both RPC
// handlers; this module is the only writer.
//
// ── What this module is, and is not ─────────────────────────────────────────
// It is the small, typed bridge between a JavaScript document and a row in the
// account blob store: canonical JSON → AES-256-GCM under a key derived from
// `derive_root` → `PUT /api/account/blobs/cid/<name>` with compare-and-set.
// It is NOT a cache, NOT a store, and NOT a sync engine. Callers hold their own
// document and its generation and decide what to do on a conflict — for a
// wallet list that is "re-read, re-apply the edit, write again" (§7.3), which
// is idempotent per slot; for a different document it might be a prompt.
//
// ── The invariant it exists to hold (§4.1) ──────────────────────────────────
// **Derived keys live in memory only, for the length of one call.** The key is
// derived, used, and zeroed in a `finally`. Nothing here caches it between
// calls, because the root is already in memory on any unlocked tab and
// re-deriving is one HKDF — cheaper than reasoning about when a cached key
// should be invalidated (lock, identity switch, logout, root rotation).
//
// ── Locked vs unlocked ──────────────────────────────────────────────────────
// Reading or writing needs the ROOT, which lives in the in-memory bag, so both
// throw `cid-locked` on a locked tab. Deleting does not: it carries no
// ciphertext, so gating it on the bag would be a lock with nothing behind it.
// The session token is what authorises all three, and the API layer supplies it.

import { canonicalize, canonicalizeToString, type CanonicalValue } from '@catalog-canonical-json';
import * as blobApi from '../api/blobs';
import type { BlobRecord, PutBlobBody, PutBlobResult } from '../api/types';
import * as aes from '../crypto/aes';
import { keys } from './keys';
import { DERIVE_ROOT_KEY_ID, DERIVE_ROOT_BYTES } from '../crypto/derive';
import {
    deriveBlobKey, encryptBlob, decryptBlob, isValidBlobName, MAX_BLOB_BASE64_CHARS,
} from '../crypto/blob';

/** The vault's reserved namespace. */
export const CID_BLOB_MODULE_ID = 'cid';

/**
 * The HTTP calls, injected so the logic is unit-testable against a fake server
 * that really enforces compare-and-set — no network, no session, no fetch.
 * Production callers omit this and get {@link defaultCidBlobDeps}.
 */
export interface CidBlobDeps {
    getBlob(moduleId: string, name: string): Promise<BlobRecord>;
    putBlob(
        moduleId: string, name: string, body: PutBlobBody, ifGeneration: number,
    ): Promise<PutBlobResult>;
    deleteBlob(moduleId: string, name: string): Promise<void>;
    /**
     * Where the derive root comes from. Omitted everywhere but one place, and
     * then it is the in-memory bag ({@link requireRoot}).
     *
     * `finalizeCredentials` is the exception: it has just WRITTEN the bag that
     * carries the root but the wizard has not yet called `keys.load`, so the
     * global bag would answer `cid-locked` for a root the caller is holding.
     * Rather than have finalize load the bag as a side effect — a side effect
     * the wizards own, and one that would run before the account's other
     * writes are done — it hands the root over here.
     */
    getRoot?(): Uint8Array;
}

export const defaultCidBlobDeps: CidBlobDeps = {
    getBlob: blobApi.getBlob,
    putBlob: blobApi.putBlob,
    deleteBlob: blobApi.deleteBlob,
};

/**
 * The account's derive root, from the in-memory bag.
 *
 * @throws Error('cid-locked') when the bag is not loaded.
 * @throws Error('derive-root-missing') when an unlocked bag carries no root —
 *         only possible for an account created before §5.1 landed, since
 *         `finalizeCredentials` now mints one at bag creation.
 * @throws Error('derive-root-malformed') on a wrong-sized root: deriving from
 *         it would produce keys no other device could reproduce.
 */
function requireRoot(): Uint8Array {
    if (!keys.isLoaded()) throw new Error('cid-locked');
    const rootB64 = keys.getPrivateKey(DERIVE_ROOT_KEY_ID);
    if (!rootB64) throw new Error('derive-root-missing');
    let root: Uint8Array;
    try {
        root = aes.fromBase64(rootB64);
    } catch {
        throw new Error('derive-root-malformed');
    }
    if (root.length !== DERIVE_ROOT_BYTES) throw new Error('derive-root-malformed');
    return root;
}

/** Derive this blob's key, run `use`, and zero the key whatever happens. */
async function withBlobKey<T>(
    name: string, deps: CidBlobDeps, use: (key: Uint8Array) => Promise<T>,
): Promise<T> {
    if (!isValidBlobName(name)) throw new Error(`blob: illegal blob name '${name}'`);
    const root = deps.getRoot ? deps.getRoot() : requireRoot();
    const key = deriveBlobKey(root, CID_BLOB_MODULE_ID, name);
    try {
        return await use(key);
    } finally {
        key.fill(0);
    }
}

/**
 * Read and decrypt one of the vault's blobs.
 *
 * @returns the document and the generation it was stored at, or **null** when
 *          the account holds no such blob. Null is a normal answer — a member
 *          who never used the feature has no row — so callers treat it as
 *          "empty document", never as an error.
 * @throws on a decryption failure, which means the stored bytes do not belong
 *         to this account's root and must NOT be silently replaced.
 */
export async function readCidBlob<T>(
    name: string,
    deps: CidBlobDeps = defaultCidBlobDeps,
): Promise<{ value: T; generation: number } | null> {
    return withBlobKey(name, deps, async (key) => {
        let row: BlobRecord;
        try {
            row = await deps.getBlob(CID_BLOB_MODULE_ID, name);
        } catch (err) {
            if (err instanceof blobApi.BlobNotFoundError) return null;
            throw err;
        }
        const plaintext = await decryptBlob(key, row.ciphertext, row.nonce);
        return {
            value: JSON.parse(aes.decodeUtf8(plaintext)) as T,
            generation: row.generation,
        };
    });
}

/**
 * Encrypt and write one of the vault's blobs, compare-and-set.
 *
 * @param ifGeneration the generation this write expects to replace; 0 creates.
 * @throws {blobApi.BlobConflictError} when another writer got there first —
 *         nothing was written, and the error carries the generation to re-read
 *         against.
 *
 * The document is serialized as RFC 8785 canonical JSON: two devices that
 * build the same document produce the same plaintext, which keeps a diff
 * meaningful and keeps key order out of the ciphertext.
 */
export async function writeCidBlob<T>(
    name: string,
    value: T,
    ifGeneration: number,
    deps: CidBlobDeps = defaultCidBlobDeps,
): Promise<{ generation: number }> {
    return withBlobKey(name, deps, async (key) => {
        const plaintext = canonicalize(value as CanonicalValue);
        const body = await encryptBlob(key, plaintext);
        // Checked here so a document the server would refuse never crosses the
        // wire (or, for a module, the dashboard bus) at all.
        if (body.ciphertext.length > MAX_BLOB_BASE64_CHARS) {
            throw new Error(
                `blob '${name}' is too large: ${body.ciphertext.length} base64 chars `
                + `(max ${MAX_BLOB_BASE64_CHARS})`);
        }
        const result = await deps.putBlob(CID_BLOB_MODULE_ID, name, body, ifGeneration);
        return { generation: result.generation };
    });
}

/**
 * Delete one of the vault's blobs. Needs no key — see the module header.
 *
 * @throws {blobApi.BlobNotFoundError} when there is nothing to delete, so a
 *         caller that believed a blob existed learns that it did not rather
 *         than recording a successful removal.
 */
export async function deleteCidBlob(
    name: string,
    deps: CidBlobDeps = defaultCidBlobDeps,
): Promise<void> {
    if (!isValidBlobName(name)) throw new Error(`blob: illegal blob name '${name}'`);
    await deps.deleteBlob(CID_BLOB_MODULE_ID, name);
}

/**
 * The exact plaintext bytes {@link writeCidBlob} would store for `value`.
 * Exported for callers that need to size a document before deciding to write
 * it, and for tests that pin a document's canonical form.
 */
export function cidBlobPlaintext<T>(value: T): string {
    return canonicalizeToString(value as CanonicalValue);
}
